/**
 * Consent-gated tracking script loader.
 *
 * Renders nothing. Side-effects only: injects/removes <script> tags depending
 * on the current cookie consent state. Designed so the page CANNOT call a
 * tracker before the visitor opted in:
 *
 *   • Google Analytics 4 (`gaId`) → fires only when consent.statistics === true
 *   • Meta Pixel       (`metaPixelId`) → fires only when consent.marketing === true
 *
 * When the user withdraws consent later (via the footer "Cookie-Einstellungen"
 * link), the corresponding script tags + globals are removed so further events
 * are NOT sent. Note that already-flushed pageviews cannot be recalled.
 *
 * Safe to mount unconditionally — the component is a no-op when no IDs are
 * configured for the current page.
 */
import { useEffect } from "react";
import { useConsent } from "@/lib/consent";

function removeNode(id) {
  const el = document.getElementById(id);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

function clearGtag() {
  removeNode("studiomv-ga4-base");
  removeNode("studiomv-ga4-init");
  try {
    // The previously declared global `gtag` function is non-configurable when
    // declared via `function gtag(){…}` in a script tag — overwrite with a
    // no-op so any stale calls become harmless and don't fall through to
    // dataLayer which may still be live in the page.
    window.gtag = function () { /* consent revoked */ };
    window.dataLayer = [];
  } catch { /* noop */ }
}

function clearPixel() {
  removeNode("studiomv-pixel-init");
  removeNode("studiomv-pixel-noscript");
  try {
    delete window.fbq;
    delete window._fbq;
  } catch { /* noop */ }
}

export default function TrackingScripts({ gaId, metaPixelId }) {
  const consent = useConsent();

  // ---- GA4 (Statistik) ----
  useEffect(() => {
    if (!gaId || !consent.statistics) { clearGtag(); return; }
    if (document.getElementById("studiomv-ga4-base")) return;
    const s1 = document.createElement("script");
    s1.id = "studiomv-ga4-base";
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.id = "studiomv-ga4-init";
    // IP-Anonymisierung ist Pflicht für DE/EU (DSGVO) → anonymize_ip + ad_storage denied.
    s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'granted'});gtag('config','${gaId}',{anonymize_ip:true});`;
    document.head.appendChild(s2);
    return () => { /* cleanup happens at next consent change above */ };
  }, [gaId, consent.statistics]);

  // ---- Meta Pixel (Marketing) ----
  useEffect(() => {
    if (!metaPixelId || !consent.marketing) { clearPixel(); return; }
    if (document.getElementById("studiomv-pixel-init")) return;
    const s = document.createElement("script");
    s.id = "studiomv-pixel-init";
    s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${metaPixelId}');fbq('track','PageView');`;
    document.head.appendChild(s);
    return () => { /* cleanup happens at next consent change above */ };
  }, [metaPixelId, consent.marketing]);

  return null;
}
